import {
    IsString,
    Length,
    IsNotEmpty,
    IsOptional,
    IsEmail,
    ValidateIf,
} from 'class-validator';
import { useState } from 'react';

import { useNotification } from 'src/components/Notification/useNotfication';
import { CreatorAvatarInput } from 'src/components/FormComponents/CreatorAvatarInput';
import { useCreatorAvatarEditor } from 'src/view/hooks/useCreatorAvatarEditor';
import container from 'src/container';
import { Creator } from 'src/domain/creator';
import { LoginContactFields } from 'src/components/AccountAccess/LoginContactFields';
import { mutate } from 'swr';
import Form from 'src/components/FormComponents/Form';
import Input from 'src/components/FormComponents/Input';
import { EnvelopeIcon, PhoneIcon } from 'src/components/Icons/icons';
import { LanguageSelect } from 'src/components/LanguageSelect/LanguageSelect';
import { dispatch } from 'src/state';
import { UserThunks } from 'src/state/thunks';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import { Section } from 'src/components/Section';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { OrganizationStructureVersion } from 'src/domain/organization';
import { isOrganizationAdministrator } from 'src/domain/user';

const {
    cradle: { munduClient },
} = container;

class EditCreatorSchema {
    @IsOptional()
    @IsString()
    @Length(2, 30, {
        message: () => i18n.t('validation.lengthBetween', { min: 2, max: 30 }),
    })
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    firstName?: string;

    @IsOptional()
    @IsString()
    @IsNotEmpty({ message: () => i18n.t('validation.requiredField') })
    lastName?: string;

    @IsOptional()
    @ValidateIf(o => o.email?.length !== 0)
    @IsString()
    @IsEmail({}, { message: () => i18n.t('validation.invalidEmail') })
    email?: string;
}

export const CreatorProfile = ({ user }: { user: Creator }) => {
    const notify = useNotification();
    const { t } = useTranslation();
    const { organization, organizationId } = useCurrentOrganization();
    const avatarEditor = useCreatorAvatarEditor(user);
    const [isSaving, setIsSaving] = useState(false);
    const canEditPicture = !user.supportMode;
    const canEditPersonalProfile =
        !user.supportMode &&
        (organization?.structureVersion ===
            OrganizationStructureVersion.separated ||
            Boolean(organization?.legacyAdministratorRetiredAt)) &&
        isOrganizationAdministrator(user, organizationId);

    const handleUpdate = async (data: EditCreatorSchema) => {
        if (!canEditPicture) return;
        setIsSaving(true);
        try {
            const avatar = await avatarEditor.getAvatarUpdate();
            if (!canEditPersonalProfile && avatar === undefined) return;
            await munduClient.updateCreator(user.id, {
                ...(canEditPersonalProfile ? data : {}),
                avatar,
            });
            await dispatch(UserThunks.refreshUser());
            await mutate([user.id, 'creators/creatorId']);
            await mutate(['creators/organizationId', user.organizationId]);
            avatarEditor.reset();
            notify(t('notifications.profileUpdated'));
        } catch (e) {
            if (e instanceof Error) notify(e.message, true);
        } finally {
            setIsSaving(false);
        }
    };
    return (
        <div className="flex flex-col gap-8 max-w-3xl mx-auto">
            <Form
                className="bg-lightPeach text-woodyBrown"
                schema={EditCreatorSchema}
                onSubmit={handleUpdate}
                mode="onChange"
                defaultValues={{
                    firstName: user.profile.firstName,
                    lastName: user.profile.lastName,
                }}
            >
                {({
                    register,
                    formState: { isValid, errors, isSubmitting },
                }) => (
                    <div className="grid grid-cols-2 gap-4 h-full">
                        <div className="col-span-full flex justify-center">
                            <CreatorAvatarInput
                                editor={avatarEditor}
                                disabled={
                                    !canEditPicture || isSubmitting || isSaving
                                }
                            />
                        </div>

                        <Input
                            className="text-input"
                            type="text"
                            label={t('profile.labels.firstName')}
                            error={errors.firstName}
                            {...register('firstName')}
                            readOnly={!canEditPersonalProfile}
                        />
                        <Input
                            className="text-input"
                            type="text"
                            label={t('profile.labels.lastName')}
                            error={errors.lastName}
                            {...register('lastName')}
                            readOnly={!canEditPersonalProfile}
                        />
                        <LoginContactFields
                            userId={user.id}
                            readOnly={Boolean(user.supportMode)}
                        />
                        {canEditPersonalProfile ||
                        (canEditPicture && avatarEditor.hasChanges) ? (
                            <div className="col-span-full flex justify-end">
                                <Input
                                    value={t('common.save')}
                                    className="primary-btn-small"
                                    disabled={
                                        (canEditPersonalProfile && !isValid) ||
                                        isSubmitting ||
                                        isSaving
                                    }
                                    type={
                                        canEditPersonalProfile
                                            ? 'submit'
                                            : 'button'
                                    }
                                    onClick={
                                        canEditPersonalProfile
                                            ? undefined
                                            : () => handleUpdate({})
                                    }
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </Form>

            <Section title={t('profile.language')}>
                <div className="items-start">
                    <LanguageSelect />
                </div>
            </Section>
        </div>
    );
};
